import { postalAddressLine } from './postalAddress';

/**
 * A place's coordinates as a person reads them and as a maps link takes them
 * (piece 10; T103). 🔨 Six decimals, which is about ten centimetres: enough to
 * tell one door of a building from another, and no more digits than a person
 * copies.
 */
export interface GeoPoint {
  latitude: number;
  longitude: number;
}

/** How many decimals a coordinate is drawn to. */
export const COORDINATE_DECIMALS = 6;

/** Both halves present and finite, or no point at all; one half alone is no place. */
export function coordinatesOf(latitude: number | null | undefined, longitude: number | null | undefined): GeoPoint | null {
  if (typeof latitude !== 'number' || typeof longitude !== 'number') return null;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
}

/** The read-only line, 40.623100, -80.255100. */
export function formatCoordinates(point: GeoPoint): string {
  return `${point.latitude.toFixed(COORDINATE_DECIMALS)}, ${point.longitude.toFixed(COORDINATE_DECIMALS)}`;
}

/** What a maps search takes for a point: the two numbers with no space, which every maps service reads as a place. */
export function coordinatesMapsQuery(point: GeoPoint): string {
  return `${point.latitude.toFixed(COORDINATE_DECIMALS)},${point.longitude.toFixed(COORDINATE_DECIMALS)}`;
}

/** The columns the precedence rule reads off a Location (decision 0674). */
export interface LocationPointSource {
  pinnedLatitude?: number | null;
  pinnedLongitude?: number | null;
  verificationStatus?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

/** Where a Location's map point came from: the pin a person placed, or the provider's verified coordinates. */
export type LocationPointSourceKind = 'pin' | 'address';

export interface LocationPoint {
  point: GeoPoint;
  source: LocationPointSourceKind;
}

/**
 * 🔒 The one precedence rule for a Location's map point (decision 0674): the
 * pin, else the verified address, else nothing. *"pinned coordinates, then
 * address, then name"* — product owner, 2026-09-23. A Location named and
 * neither pinned nor verified has no point. Every reader of a Location's
 * coordinates calls this; none reads the columns itself.
 */
export function locationPoint(location: LocationPointSource | null | undefined): LocationPoint | null {
  if (!location) return null;
  const pin = coordinatesOf(location.pinnedLatitude, location.pinnedLongitude);
  if (pin) return { point: pin, source: 'pin' };
  // 🔒 An Estimated address unlocks the point as a Verified one does (decision 7460).
  const verified = location.verificationStatus === 'Verified' || location.verificationStatus === 'Estimated' ? coordinatesOf(location.latitude, location.longitude) : null;
  return verified ? { point: verified, source: 'address' } : null;
}

/** A point a browser handed over, checked before it is stored: both halves finite and on the globe. */
export function pinProblems(latitude: unknown, longitude: unknown): string[] {
  const problems: string[] = [];
  if (typeof latitude !== 'number' || !Number.isFinite(latitude) || Math.abs(latitude) > 90) problems.push('a pin needs a latitude between -90 and 90');
  if (typeof longitude !== 'number' || !Number.isFinite(longitude) || Math.abs(longitude) > 180) problems.push('a pin needs a longitude between -180 and 180');
  return problems;
}

/**
 * A Location's Open in Maps (decision 0674): the pin's point where somebody
 * pinned one, else the one-line address, else nothing. 🔨 A name is never
 * handed over — a name is a search and an address is a place (product owner,
 * 2026-09-10) — so a place named and not pinned draws no map button.
 */
export function locationMapsQuery(location: (LocationPointSource & { addressLines?: readonly string[] | null; locality?: string | null; administrativeArea?: string | null; postalCode?: string | null }) | null | undefined): string | null {
  if (!location) return null;
  const point = locationPoint(location);
  if (point?.source === 'pin') return coordinatesMapsQuery(point.point);
  return postalAddressLine(location);
}

/**
 * Why a browser's location reading gave no point (decisions 0674 and 3440). 🔒 An address the browser does not count
 * as secure is named as that, never as a refused permission, because the person can allow nothing to fix it.
 */
export type GeolocationRefusal = 'denied' | 'insecure' | 'unavailable' | 'timeout' | 'unsupported';

/**
 * A browser's error code as the refusal it names: 1 is permission denied, 2 position unavailable, 3 timeout. 🔒 A page
 * on an address that is not secure is refused whatever the code says, so the refusal is named for the address
 * (decision 3440).
 */
export function geolocationRefusal(code: number | null | undefined, secureContext = true): GeolocationRefusal {
  if (!secureContext) return 'insecure';
  if (code === 1) return 'denied';
  if (code === 3) return 'timeout';
  if (code === 2) return 'unavailable';
  return 'unsupported';
}

/** The sentence each refusal reads as, so a person learns whether to allow, change the address, move or wait. */
export const GEOLOCATION_REFUSAL_KEYS = {
  denied: 'geolocation.denied',
  insecure: 'geolocation.insecure',
  unavailable: 'geolocation.unavailable',
  timeout: 'geolocation.timeout',
  unsupported: 'geolocation.unsupported',
} as const satisfies Record<GeolocationRefusal, string>;
